<template>
    <div class="l-wrapper__inner">
        <loader ref="loader" />
        <main class="l-main" ref="main">
            <router-view v-if="page" :page="page.data" />
        </main>

        <app-footer :class="{ 'is-visible': showFooter }" />
    </div>
</template>

<script>
import { mapState } from "vuex";

import AppFooter from "layout/AppFooter";
import Loader from "objects/Loader";

import Variables from "src/mixins/variables";
import { EventBus } from "src/event-bus.js";

import debounce from "lodash/debounce";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin);

export default {
    name: "AppBase",
    components: {
        AppFooter,
        Loader
    },
    props: {
        page: Object
    },
    mixins: [Variables],
    data: () => ({
        scroll: {
            x: 0,
            y: 0
        },
        scrollLength: 0,
        scrollEnd: false,
        showFooter: false
    }),
    computed: {
        ...mapState({
            isDark: state => state.navigation.isDark
        })
    },
    mounted() {
        // Lazy 404 detection
        if (!this.page) {
            this.$nextTick(() => {
                this.$router.push({ path: "/" });
            });
        }

        this.bindEvents();

        // Loader animation start
        EventBus.$on("loader-start", next => {
            this.scrollEnd = false;
            this.unbindEvents();
            this.$refs.loader.animateMainIn(next);

            if (this.isHorizontal) {
                gsap.to(this.$el, {
                    duration: 1,
                    scrollTo: {
                        x: this.scroll.x > this.W.w ? this.scroll.x - this.W.w : 0
                    },
                    ease: "power2.in",
                    onStart: () => {
                        document.body.removeEventListener("wheel", this.scrollEventX);
                    },
                    onComplete: () => {
                        document.body.addEventListener("wheel", this.scrollEventX);
                        gsap.set(this.$el, {
                            scrollTo: {
                                x: 0
                            }
                        });
                    }
                });
            } else {
                gsap.to(window, {
                    duration: 1,
                    scrollTo: {
                        y: this.scroll.y > this.W.h ? this.scroll.y - this.W.h : 0
                    },
                    ease: "power2.in",
                    onComplete: () => {
                        gsap.set(window, {
                            scrollTo: {
                                y: 0
                            }
                        });
                    }
                });
            }
        });

        EventBus.$on("loader-complete", () => {
            this.bindEvents();
            this.setScrollLength();
        });

        // Window events
        window.addEventListener("resize", debounce(this.setScrollLength, 100));
    },
    methods: {
        scrollEventX(e) {
            this.scroll.x = this.$el.scrollLeft;
            this.scrollEnd = Math.ceil(this.scroll.x + this.W.w) >= this.scrollLength - 5;

            let delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

            // if (this.browser.name === "firefox") {
            //delta = e.deltaY > 0 ? Math.min(e.deltaY * 5, 200) : Math.max(e.deltaY * 5, -200)
            // }

            gsap.to(this.$el, {
                duration: 0, // duration over 0.1 will lead to glitch, even if the FPS === 1
                scrollTo: {
                    x: `+=${delta}`
                },
                //overwrite: this.browser.name === 'firefox' ? 'all' : 'auto',
                ease: "sine.out"
            });
            this.isHeaderNeedBackground(this.scroll.x);
        },
        scrollEventY() {
            this.scroll.y = window.pageYOffset + 150;
            this.scrollEnd = Math.ceil(this.scroll.y + this.W.h) >= this.scrollLength - 5;
            this.isHeaderNeedBackground(this.scroll.y);
        },
        setScrollLength() {
            this.scrollLength = this.ifHorizontal(this.$el.scrollWidth, document.body.offsetHeight);
        },
        bindEvents() {
            this.setScrollLength();
            if (this.isHorizontal) {
                document.body.addEventListener("wheel", this.scrollEventX);
                window.removeEventListener("scroll", this.scrollEventY);
                this.scrollEventX({ deltaY: 0 });
            } else {
                document.body.removeEventListener("wheel", this.scrollEventX);
                window.addEventListener("scroll", this.scrollEventY);
                this.scrollEventY();
            }
        },
        unbindEvents() {
            if (this.isHorizontal) {
                document.body.removeEventListener("wheel", this.scrollEventX);
                this.toggleNavColorGlobally(false);
            } else {
                window.removeEventListener("scroll", this.scrollEventY);
                this.toggleNavColorGlobally(false);
            }
        },

        ////////////////////////////////
        //       START UPDATE NAV COLOR
        ////////////////////////////////

        isHeaderNeedBackground(scrollValue) {
            // I think it was build in a way that only one direction can be used at a time
            return scrollValue >= 100 ? this.updateNavColor(true) : this.updateNavColor(false);
        },

        updateNavColor(bool) {
            // check if color is already update
            // if its not the same so update
            return this.isDark !== bool ? this.toggleNavColorGlobally(bool) : null;
        },
        toggleNavColorGlobally(bool) {
            // check if color is already update
            this.$store.commit("navigation/TOGGLE_DARK", bool);
        }
        ////////////////////////////////
        //       END UPDATE NAV COLOR
        ////////////////////////////////
    },
    watch: {
        scrollEnd() {
            // console.log("this.scrollEnd", this.scrollEnd);
            if (this.scrollEnd && !this.showFooter) {
                this.showFooter = true;
            } else if (!this.scrollEnd && this.showFooter) {
                this.showFooter = false;
            }
        },
        isHorizontal() {
            this.bindEvents();
        }
    }
};
</script>

<style lang="scss">
.l-wrapper__inner {
    position: relative;
}
</style>
